import React, { useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { createAdminUser } from '../commonApi';

// "Add User" popup on the Users page. Creates the user in the smart-home-admin-portal Keycloak
// realm with one role (ADMIN / OPERATOR / QA) via POST device_mgmt_service/admin/users.
export const STAFF_ROLES = ['ADMIN', 'OPERATOR', 'QA'];
export const ROLE_OPTIONS = [
    { value: 'ADMIN', label: 'Admin' },
    { value: 'OPERATOR', label: 'Operator' },
    { value: 'QA', label: 'QA' },
];

// Same rules the backend checks: upper, lower, digit, special, no whitespace, min 12 chars.
// Look-alike characters (O/0, l/1, I) are left out so the password is easy to read out.
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const DIGITS = '23456789';
const SPECIAL = '!@#$%^&*-_=+';
export const PASSWORD_LENGTH = 16;

const randomIndex = (max) => {
    const buffer = new Uint32Array(1);
    window.crypto.getRandomValues(buffer);
    return buffer[0] % max;
};

export const generatePassword = (length = PASSWORD_LENGTH) => {
    const all = UPPER + LOWER + DIGITS + SPECIAL;
    const chars = [UPPER, LOWER, DIGITS, SPECIAL].map((set) => set[randomIndex(set.length)]);
    while (chars.length < length) chars.push(all[randomIndex(all.length)]);
    for (let i = chars.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
};

const emptyForm = () => ({ email: '', firstName: '', lastName: '', role: '', password: generatePassword() });
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateForm = (form) => {
    const errors = {};
    if (!form.email.trim()) errors.email = 'Please enter an email';
    else if (!EMAIL_PATTERN.test(form.email.trim())) errors.email = 'Please enter a valid email';
    if (!form.firstName.trim()) errors.firstName = 'Please enter a first name';
    if (!form.lastName.trim()) errors.lastName = 'Please enter a last name';
    if (!form.role) errors.role = 'Please select a role';
    if (!form.password) errors.password = 'Please generate a password';
    return errors;
};

// Turns the backend error (ApiResponse / DetailedErrorResponse) into a user-facing message.
export const errorMessageFrom = (error) => {
    const status = error?.response?.status;
    const apiError = error?.response?.data?.error;
    if (status === 409) {
        return apiError?.message || 'A user with this email already exists';
    }
    if (status === 400) {
        const details = apiError?.details;
        if (details && typeof details === 'object') {
            const messages = Object.values(details).filter(Boolean);
            if (messages.length > 0) return messages.join('. ');
        }
        return apiError?.message || 'Please check the entered details';
    }
    if (status === 401) return 'Your session has expired. Please log in again.';
    if (status === 403) return 'Only ADMIN users can create users';
    return 'Failed to create user. Please try again.';
};

function CreateUserModal({ onClose, onCreated }) {
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    // { email, password } of the user just created. Lives only in this component's state:
    // never stored in localStorage/redux, gone as soon as the popup closes.
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);

    const closeModal = () => {
        if (saving) return;
        onClose();
    };

    const handleChange = (field) => (event) => {
        const { value } = event.target;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const regeneratePassword = () => {
        setForm((prev) => ({ ...prev, password: generatePassword() }));
        setErrors((prev) => ({ ...prev, password: undefined }));
    };

    const handleRoleChange = (selected) => {
        setForm((prev) => ({ ...prev, role: selected?.value || '' }));
        setErrors((prev) => ({ ...prev, role: undefined }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (saving) return;

        const validationErrors = validateForm(form);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                email: form.email.trim(),
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                // One role per user; the API takes a list, so send it as a single-item list.
                roles: [form.role],
                password: form.password,
            };
            const response = await createAdminUser(payload);
            const data = response?.data?.data;
            setCreated({
                email: data?.email || payload.email,
                password: data?.generatedPassword || payload.password,
            });
            toast.success('User created successfully');
            if (onCreated) onCreated();
        } catch (error) {
            toast.error(errorMessageFrom(error));
        } finally {
            setSaving(false);
        }
    };

    const handleCopy = async () => {
        if (!created || !navigator?.clipboard) return;
        try {
            await navigator.clipboard.writeText(`Email: ${created.email}\nPassword: ${created.password}`);
            setCopied(true);
        } catch {
            toast.error('Could not copy. Please copy the password manually.');
        }
    };

    const selectedRoleOption = ROLE_OPTIONS.find((o) => o.value === form.role) || null;

    return (
        <>
        <div className="modal fade show" style={{ display: 'block' }} role="dialog" aria-modal="true" aria-labelledby="staff-user-modal-title">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="staff-user-modal-title">
                            {created ? 'User Created' : 'Add User'}
                        </p>
                        <button type="button" className="btn-close" aria-label="Close" onClick={closeModal} disabled={saving} />
                    </div>

                    {created ? (
                        <>
                            <div className="modal-body">
                                <div className="alert alert-success mb-0" role="status" data-testid="created-user-banner">
                                    <p className="fw-bold mb-2">Share these credentials with the user now.</p>
                                    <p className="mb-1">Email: <code>{created.email}</code></p>
                                    <p className="mb-2">Password: <code>{created.password}</code></p>
                                    <p className="mb-0 small">
                                        This password is shown only once and cannot be viewed again. If it is
                                        lost, reset it in Keycloak.
                                    </p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn light_primary_btn me-2" onClick={closeModal}>
                                    Cancel
                                </button>
                                <button type="button" className="btn primary_btn" onClick={handleCopy}>
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label" htmlFor="staff-email">
                                            Email<span style={{ color: 'red' }}>*</span>
                                        </label>
                                        <input id="staff-email" type="email" className="form-control"
                                            placeholder="Please add Email" value={form.email} onChange={handleChange('email')} />
                                        <p className="error_msg">{errors.email}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label" htmlFor="staff-password">
                                            Password<span style={{ color: 'red' }}>*</span>
                                        </label>
                                        <div className="input-group">
                                            <input id="staff-password" type="text" className="form-control"
                                                value={form.password} readOnly
                                                style={{ fontFamily: 'monospace', backgroundColor: 'var(--white-100)' }} />
                                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-regenerate-password">Generate new password</Tooltip>}>
                                                <button type="button" className="btn light_primary_btn" aria-label="Generate new password"
                                                    onClick={regeneratePassword} disabled={saving}>
                                                    <FontAwesomeIcon icon={faArrowsRotate} />
                                                </button>
                                            </OverlayTrigger>
                                        </div>
                                        <p className="error_msg">{errors.password}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label" htmlFor="staff-first-name">
                                            First Name<span style={{ color: 'red' }}>*</span>
                                        </label>
                                        <input id="staff-first-name" type="text" className="form-control"
                                            placeholder="Please add First Name" value={form.firstName} onChange={handleChange('firstName')} />
                                        <p className="error_msg">{errors.firstName}</p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label" htmlFor="staff-last-name">
                                            Last Name<span style={{ color: 'red' }}>*</span>
                                        </label>
                                        <input id="staff-last-name" type="text" className="form-control"
                                            placeholder="Please add Last Name" value={form.lastName} onChange={handleChange('lastName')} />
                                        <p className="error_msg">{errors.lastName}</p>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                        <label className="form-label" htmlFor="staff-roles">
                                            Role<span style={{ color: 'red' }}>*</span>
                                        </label>
                                        <Select
                                            inputId="staff-roles"
                                            classNamePrefix="staff-roles"
                                            options={ROLE_OPTIONS}
                                            value={selectedRoleOption}
                                            onChange={handleRoleChange}
                                            placeholder="Select role"
                                        />
                                        <p className="error_msg">{errors.role}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn light_primary_btn me-2" onClick={closeModal} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn primary_btn" disabled={saving}>
                                    {saving ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
        <div className="modal-backdrop fade show" />
        </>
    );
}

export default CreateUserModal;
